import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Bot,
  BriefcaseBusiness,
  Bell,
  CheckCircle,
  ChevronDown,
  ExternalLink,
  Heart,
  Lock,
  Menu,
  MessageCircle,
  MessageSquare,
  Palette,
  ShieldCheck,
  ThumbsUp,
  User,
  UserCog,
  UserPlus,
  Clock,
  LogOut,
  Users,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { logoutUser } from '@/utils/logout';
import Sidebar from './common/Sidebar';
import { useFieldLabelResolver } from '../hooks/useFieldLabel';
import { useAppDispatch, useAppSelector } from '../store';
import { auth } from '../firebase';
import config from '../config/config';
import apiClient from '../services/api/config';
import { logout } from '../store/actions/authActions';
import { Admin_GetMessageList, Admin_GetNotifications, Admin_MarkNotificationRead } from '../constants/adminData';
import type { NotificationItem } from '../types/admin';
import { normalizeNotificationType, notificationTypes } from '../constants/notificationTypes';
import { getSensitiveData } from '../utils/secureStorage';
import UnsavedChangesDialog from './common/UnsavedChangesDialog';
import { decrementUnreadNotificationsCount, refreshNotifications, setUnreadNotificationsCount } from '../redux/slices/userProfileSlice';
import Toast from './Toast/Toast';
import MessageChat from '../pages/figma/UserInfo/messageChat/MessageChat';
import { PhotoImage } from './common/PhotoImage';

interface DashboardLayoutProps {
  readonly children: React.ReactNode;
  readonly title?: string;
  readonly contentClassName?: string;
}

interface ActiveChat {
  receiverId: string;
  userName: string;
  photoId?: string;
  scrollToMessageId?: number;
}

export default function DashboardLayout({ children, title, contentClassName }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigationHref, setPendingNavigationHref] = useState<string | null>(null);
  const [notificationTab, setNotificationTab] = useState<'all' | 'unread'>('all');
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [activeChat, setActiveChat] = useState<ActiveChat | null>(null);
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const markingNotificationIdsRef = useRef(new Set<number>());
  const profileRef = useRef<HTMLDivElement | null>(null);
  const mainContentRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    mainContentRef.current?.scrollTo(0, 0);
  }, [location.pathname]);
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.userProfile);
  const { theme } = useTheme();
  const getLabel = useFieldLabelResolver();
  const resolvedTitle = getLabel(title || 'Dashboard', title || 'Dashboard');
  const isProfileIncomplete = profile.isProfileIncomplete;
  const firstName = profile.personalInfo?.firstName || '';
  const lastName = profile.personalInfo?.lastName || '';
  const userName = `${firstName} ${lastName}`.trim() || 'User';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || userName.slice(0, 2).toUpperCase();
  const profileImage = profile.personalInfo?.profileImage;
  const profilePhotoId = profile.personalInfo?.photoId;
  const unreadCount = notifications.length
    ? notifications.filter((notification) => !notification.isRead).length
    : profile.unreadNotificationsCount || 0;
  const displayedUnreadCount = unreadCount > 99 ? '99+' : String(unreadCount);
  const visibleNotifications = useMemo(
    () => notificationTab === 'unread' ? notifications.filter((notification) => !notification.isRead) : notifications,
    [notificationTab, notifications],
  );

  useEffect(() => {
    const loadAuthToken = async () => {
      const token = profile.authToken || (await getSensitiveData('authToken')) || '';
      setAuthToken(token);
    };
    loadAuthToken();
  }, [profile.authToken]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (notificationRef.current && !notificationRef.current.contains(target)) {
        setNotificationsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(target)) {
        setProfileOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!notificationsOpen || !authToken) return;

    const loadNotifications = async () => {
      try {
        setNotificationsLoading(true);
        const response = await Admin_GetNotifications(authToken);
        if (Array.isArray(response)) {
          setNotifications(response);
          dispatch(setUnreadNotificationsCount(response.filter((notification) => !notification.isRead).length));
        } else if (response?.success && response.data?.notifications && Array.isArray(response.data.notifications)) {
          setNotifications(response.data.notifications);
          dispatch(setUnreadNotificationsCount(response.data.unreadCount));
        } else if (response?.success && Array.isArray(response.data)) {
          setNotifications(response.data);
          dispatch(setUnreadNotificationsCount(response.data.filter((notification) => !notification.isRead).length));
        } else {
          setNotifications([]);
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    };

    loadNotifications();
  }, [authToken, dispatch, notificationsOpen, profile.notificationRefreshVersion]);

  const markNotificationRead = async (notification: NotificationItem) => {
    if (notification.isRead || !authToken || markingNotificationIdsRef.current.has(notification.notificationId)) return;

    markingNotificationIdsRef.current.add(notification.notificationId);
    try {
      await Admin_MarkNotificationRead(String(notification.notificationId), authToken);
      setNotifications((current) => current.map((item) =>
        item.notificationId === notification.notificationId ? { ...item, isRead: true } : item
      ));
      dispatch(decrementUnreadNotificationsCount());
      dispatch(refreshNotifications());
    } catch (error) {
      console.error('Failed to mark notification read:', error);
      setToast({ message: 'Failed to mark notification as read. Please try again.', type: 'error' });
    } finally {
      markingNotificationIdsRef.current.delete(notification.notificationId);
    }
  };

  const handleNavigation = async (href: string) => {
    const hasUnsavedChanges = Boolean((globalThis as typeof globalThis & { __ISX_HAS_UNSAVED_CHANGES__?: boolean }).__ISX_HAS_UNSAVED_CHANGES__);

    if (hasUnsavedChanges && href !== location.pathname) {
      setPendingNavigationHref(href);
      setShowUnsavedDialog(true);
      setSidebarOpen(false);
      return;
    }

    if (href === '/logout') {
      setSidebarOpen(false);
      try {
        await logoutUser();
        await dispatch(logout()).unwrap();
        navigate('/', { replace: true });
      } catch (error) {
        console.error('Logout failed', error);
      }
      return;
    }

    navigate(href);
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const handleLeaveWithoutSaving = () => {
    if (!pendingNavigationHref) {
      setShowUnsavedDialog(false);
      return;
    }

    (globalThis as typeof globalThis & { __ISX_HAS_UNSAVED_CHANGES__?: boolean }).__ISX_HAS_UNSAVED_CHANGES__ = false;
    const nextHref = pendingNavigationHref;
    setShowUnsavedDialog(false);
    setPendingNavigationHref(null);
    navigate(nextHref);
  };

  const handleSaveAndContinue = () => {
    if (!pendingNavigationHref) {
      setShowUnsavedDialog(false);
      return;
    }

    globalThis.dispatchEvent(
      new CustomEvent('isx-unsaved-save-request', {
        detail: { nextHref: pendingNavigationHref },
      }),
    );
    setShowUnsavedDialog(false);
    setPendingNavigationHref(null);
  };

  const handleProfileNavigation = async (href: string) => {
    setProfileOpen(false);
    await handleNavigation(href);
  };

  const getNotificationIcon = (type: string) => {
    const normalizedType = type.toLowerCase();
    if (normalizedType.includes('ai')) return Bot;
    if (normalizedType.includes('project')) return BriefcaseBusiness;
    if (normalizedType.includes('comment')) return MessageCircle;
    if (normalizedType.includes('like') || normalizedType.includes('reaction')) return Heart;
    if (normalizedType.includes('group') || normalizedType.includes('community')) return Users;
    if (normalizedType.includes('message') || normalizedType.includes('conversation')) return MessageSquare;
    if (normalizedType.includes('match') || normalizedType.includes('connection') || normalizedType.includes('profile')) return UserPlus;

    switch (type) {
      case 'profile_image_interaction':
      case 'profile_viewed':
        return UserPlus;
      case 'new_match':
      case 'updated_matches':
      case 'message_reaction':
        return ThumbsUp;
      case 'conversation_initiated':
      case 'new_message':
      case 'new_group_message':
        return MessageSquare;
      case 'subscription_expired':
      case 'account_warning':
        return AlertCircle;
      case 'subscription_activated':
      default:
        return CheckCircle;
    }
  };

const formatNotificationType = (type: string) => {
  // Convert to uppercase right away to eliminate casing bugs
  const upperType = type.toUpperCase();
  let words = upperType.split('_');
  
  // Case-insensitive check for our specific message targets
  const isMessageType = upperType.includes('NEW_MESSAGE') || upperType.includes('NEW_GROUP_MESSAGE');

  if (isMessageType) {
    // Both 'NOTIFICATION' and 'NEW' are now guaranteed to match perfectly
    words = words.filter(word => word !== 'NOTIFICATION' && word !== 'NEW');
  }

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

  const formatRelativeTime = (value: string) => {
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return '';
    const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    if (diffSeconds < 60) return 'Just now';
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return new Date(value).toLocaleDateString();
  };

  function parseApiDate(dateString: string): Date {
  if (!dateString) return new Date();

  // If backend did not send timezone, assume it's UTC and add "Z"
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(dateString);
  const safe = hasTimezone ? dateString : `${dateString}Z`;

  return new Date(safe);
}

   const formatDate = (date: Date | string): string => {

  
  const d =
    typeof date === 'string'
      ? parseApiDate(date)
      : date;
  
  const now = new Date();

  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

  const navigateToPersonalChat = async (referenceId: number, scrollToMessageId?: number) => {
    const response = await Admin_GetMessageList(authToken);
    const conversations = response?.data ?? [];
    const conversation = Array.isArray(conversations)
      ? conversations.find((item) => item.conversationId === referenceId)
      : undefined;

    if (conversation) {
      setActiveChat({
        receiverId: conversation.otherUserId,
        userName: conversation.otherUserName,
        photoId: conversation.photoId,
        scrollToMessageId,
      });
    } else {
      navigate('/OverViewMessages');
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    setNotificationsOpen(false);
    await markNotificationRead(notification);
    const normalizedNotificationType = normalizeNotificationType(notification.notificationType);

    if (normalizedNotificationType === notificationTypes.availabilityUpdatedByEmployee) {
      navigate('/availability-approvals', {
        state: notification.referenceId ? { openAvailabilityId: notification.referenceId } : undefined,
      });
      return;
    }

    if (normalizedNotificationType === notificationTypes.availabilityUpdatedByApprover) {
      navigate('/IT-AvailabilityType', { state: { section: 'availability' } });
      return;
    }

    const isPersonalMessage =
      notification.notificationType === 'new_message' ||
      notification.notificationType === 'conversation_initiated' ||
      notification.notificationType === 'message_reaction';

    if (isPersonalMessage) {
      if (notification.referenceId) {
        await navigateToPersonalChat(notification.referenceId, notification.childReferenceId ?? undefined);
      } else {
        navigate('/OverViewMessages');
      }
      return;
    }

    if (notification.notificationType === 'new_group_message') {
      navigate(notification.referenceId ? `/groups/${notification.referenceId}` : '/groups', {
        state: notification.childReferenceId ? { scrollToMessageId: notification.childReferenceId } : undefined,
      });
      return;
    }

    if (notification.navigationUrl) {
      navigate(notification.navigationUrl);
      return;
    }

    switch (notification.notificationType) {
      case 'profile_image_interaction':
      case 'profile_viewed':
        navigate('/my-profile');
        break;
      case 'new_match':
      case 'updated_matches':
        navigate('/MatchProfile');
        break;
      case 'subscription_activated':
      case 'subscription_expired':
        navigate('/menu');
        break;
      default:
        break;
    }
  };

  const handleAdminRedirect = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      // Don't hand the admin app our own Firebase ID token: the AdminPanel/AdminOnly
      // API policies need "client"/"role" claims baked into the token at mint time, and
      // baking them in here would mean overwriting this Firebase account's persisted
      // custom claims (client=WebApp, planId, ...) — which would then corrupt the WEB
      // app's own session the next time it refreshes its token (e.g. on a hard reload).
      // Instead, ask the API for a short-lived custom token scoped to this SSO handoff
      // only; the admin app exchanges it for its own ID token client-side.
      const { data } = await apiClient.get<{ data: string }>('admin/AdminUsers/sso-custom-token');
      const customToken = data.data;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${config.ADMIN_APP_URL}Account/SsoLogin`;
      form.target = '_blank';
      form.style.display = 'none';

      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'customToken';
      tokenInput.value = customToken;
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('Failed to redirect to admin app:', error);
      setToast({ message: 'Unable to open admin panel. Please try again.', type: 'error' });
    }
  };

  const HeaderActions = () => (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={handleAdminRedirect}
        className="isx-admin-trigger relative flex h-10 w-10 items-center justify-center rounded-full border transition-colors"
        style={{ backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.primary }}
        aria-label="Admin panel"
        title="Admin panel"
      >
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="relative" ref={notificationRef}>
        <button
          type="button"
          onClick={() => {
            setNotificationsOpen((isOpen) => !isOpen);
            setProfileOpen(false);
          }}
          className="isx-notification-trigger relative flex h-10 w-10 items-center justify-center rounded-full border transition-colors"
          style={{ backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.primary }}
          aria-label="Notifications"
          aria-expanded={notificationsOpen}
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
              {displayedUnreadCount}
            </span>
          ) : null}
        </button>

        {notificationsOpen ? (
          <div
            className="fixed left-3 right-3 top-16 z-[90] flex max-h-[520px] flex-col overflow-hidden rounded-2xl border bg-white shadow-[0_18px_45px_rgba(15,23,42,0.18)] sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-[400px] sm:max-w-[420px]"
            style={{ backgroundColor: theme.surface, borderColor: theme.border }}
          >
            <div className="shrink-0 border-b px-4 py-3" style={{ borderColor: theme.border }}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold leading-6" style={{ color: theme.text }}>Notifications</h2>
                  <p className="text-xs" style={{ color: theme.textSecondary }}>{unreadCount} unread</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {unreadCount > 0 ? (
                    <span className="rounded-full px-2.5 py-1 text-xs font-semibold ring-1" style={{ backgroundColor: 'var(--color-primary-100)', color: theme.primary, borderColor: 'var(--color-primary-200)' }}>
                      {displayedUnreadCount}
                    </span>
                  ) : null}
                  {/* <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-slate-100 hover:text-gray-700"
                    aria-label="Notification settings"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-slate-100 hover:text-gray-700"
                    aria-label="More notification actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button> */}
                </div>
              </div>
              <div className="mt-3 grid w-fit grid-cols-2 rounded-lg p-0.5" style={{ backgroundColor: theme.surfaceHover }}>
                {(['all', 'unread'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setNotificationTab(tab)}
                    className="rounded-md px-3 py-1 text-xs font-semibold capitalize transition-colors"
                    style={{
                      backgroundColor: notificationTab === tab ? theme.primary : 'transparent',
                      color: notificationTab === tab ? theme.contrastText : theme.textSecondary,
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-[180px] flex-1 overflow-y-auto px-2 py-2 custom-scrollbar">
              {notificationsLoading ? (
                <div className="space-y-2 p-2">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-3">
                      <span className="isx-skeleton h-9 w-9 rounded-lg" />
                      <span className="flex-1 space-y-2">
                        <span className="isx-skeleton block h-3 w-2/3" />
                        <span className="isx-skeleton block h-3 w-full" />
                      </span>
                    </div>
                  ))}
                </div>
              ) : visibleNotifications.length === 0 ? (
                <div className="flex h-48 flex-col items-center justify-center px-6 text-center">
                  <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100">
                    <Bell className="h-5 w-5 text-slate-400" />
                  </span>
                  <p className="text-sm font-semibold" style={{ color: theme.text }}>No notifications</p>
                  <p className="mt-1 text-sm text-gray-500">You're all caught up.</p>
                </div>
              ) : (
                visibleNotifications.map((notification) => {
                  const Icon = getNotificationIcon(notification.notificationType);
                  const title = formatNotificationType(notification.notificationType);
                  return (
                    <button
                      key={notification.notificationId}
                      type="button"
                      onClick={() => handleNotificationClick(notification)}
                      className="isx-notification-item mb-1.5 flex w-full items-start gap-2.5 rounded-xl border px-2.5 py-2.5 text-left transition-colors"
                      style={{
                        backgroundColor: notification.isRead ? theme.surface : theme.surfaceHover,
                        borderColor: notification.isRead ? 'transparent' : theme.primaryLight,
                      }}
                    >
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ring-1" style={{ backgroundColor: 'var(--color-primary-100)', color: theme.primary, borderColor: 'var(--color-primary-200)' }}>
                        <Icon className="h-4.5 w-4.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-2">
                          <span className="block truncate text-sm font-semibold leading-5" style={{ color: theme.text }}>
                            {title}
                          </span>
                          <span className="flex-shrink-0 whitespace-nowrap text-[11px] font-medium" style={{ color: theme.textSecondary }}>{formatDate(notification.createdAt.toLocaleString())}</span>
                        </span>
                        <span className="mt-0.5 line-clamp-2 text-xs leading-5" style={{ color: theme.textSecondary }}>
                          {notification.notificationMessage}
                        </span>
                      </span>
                      {!notification.isRead ? <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: theme.primary }} /> : null}
                    </button>
                  );
                })
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setNotificationsOpen(false);
                navigate('/notifications');
              }}
              className="flex shrink-0 items-center justify-center gap-1 border-t px-4 py-3 text-center text-sm font-semibold transition-colors hover:bg-slate-50"
              style={{ borderColor: theme.border, color: theme.primary }}
            >
              View All Notifications <ExternalLink className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>

      <div className="relative" ref={profileRef}>
        {/* <button
          type="button"
          onClick={() => {
            setProfileOpen((isOpen) => !isOpen);
            setNotificationsOpen(false);
          }}
          className="flex min-w-0 items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3 transition-colors hover:bg-slate-50"
          style={{ borderColor: theme.border, color: theme.text }}
          aria-label="Profile menu"
          aria-expanded={profileOpen}
        >
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white" style={{ backgroundColor: theme.primary }}>
            {profileImage ? <img src={profileImage} alt="" className="h-full w-full object-cover"  style={{color:theme.text}}/> : initials}
          </span>
          <span className="hidden max-w-[160px] truncate text-sm font-semibold sm:inline"  style={{color:theme.text}}>{userName}</span>
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        </button> */}

        <button
          type="button"
          onClick={() => {
            setProfileOpen((isOpen) => !isOpen);
            setNotificationsOpen(false);
          }}
            style={{ borderColor: theme.border, color: theme.text }}
          aria-label="Profile menu"
          aria-expanded={profileOpen}
  className="isx-profile-trigger group flex items-center gap-2 rounded-full px-3 py-2 transition-colors"
>
  <span
    className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white"
    style={{ backgroundColor: theme.primary }}
  >
    {profilePhotoId ? (
      <PhotoImage
        photoId={profilePhotoId}
        alt={userName}
        className="h-full w-full object-cover"
        fallback={<span className="flex h-full w-full items-center justify-center">{initials}</span>}
      />
    ) : profileImage ? (
      <img
        src={profileImage}
        alt=""
        className="h-full w-full object-cover"
      />
    ) : (
      initials
    )}
  </span>

  <span
    className="
      hidden max-w-[160px] truncate text-sm font-semibold sm:inline
      text-gray-900
      group-hover:text-black
    "
  >
    {userName}
  </span>

  <ChevronDown
    className="
      h-4 w-4 flex-shrink-0
      text-gray-700
      group-hover:text-black
    "
  />
</button>

        {profileOpen ? (
          <div
            className="absolute right-0 top-12 z-[90] w-64 overflow-hidden rounded-xl border py-2 shadow-2xl"
            style={{ backgroundColor: theme.surface, borderColor: theme.border }}
          >
            {[
              { label: 'My Profile', href: '/my-profile', icon: User },
              { label: 'Edit Profile', href: '/personal-details', icon: UserCog, alwaysEnabled: true },
              { label: 'My Availability', href: '/IT-AvailabilityType', icon: Clock },
              { label: 'Theme Settings', href: '/theme-settings', icon: Palette },
              { label: 'Change Password', href: '/change-password', icon: Lock },
              { label: 'Logout', href: '/logout', icon: LogOut, alwaysEnabled: true },
            ].map((item) => {
              const Icon = item.icon;
              const isDisabled = isProfileIncomplete && !item.alwaysEnabled;
              const isSelected = item.href !== '/logout' && location.pathname === item.href;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => !isDisabled && handleProfileNavigation(item.href)}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  title={isDisabled ? 'Complete your profile to unlock this' : undefined}
                  className={`isx-profile-menu-item flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                    isSelected ? 'is-selected' : ''
                  } ${
                    isDisabled ? 'cursor-not-allowed' : ''
                  }`}
                  aria-current={isSelected ? 'page' : undefined}
                  style={{
                    backgroundColor: isDisabled ? theme.surfaceHover : undefined,
                    color: item.href === '/logout' ? '#dc2626' : isDisabled ? theme.textDisabled : theme.text,
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="isx-dashboard-shell h-full min-h-0 flex" style={{ backgroundColor: theme.background, color: theme.text }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 lg:hidden bg-black bg-opacity-50"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        collapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapse={toggleSidebar}
        onNavigate={handleNavigation}
      />

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-w-0 min-h-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Top bar for mobile */}
        <header className="fixed inset-x-0 top-0 z-30 shrink-0 shadow-sm border-b lg:static" style={{ backgroundColor: theme.surface, borderBottomColor: theme.border }}>
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md transition-colors lg:hidden"
              style={{ color: theme.text }}
              onMouseEnter={(e) => e.currentTarget.style.color = theme.primary}
              onMouseLeave={(e) => e.currentTarget.style.color = theme.text}
              aria-label="Open sidebar"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="min-w-0 flex-1 truncate px-4 text-lg font-semibold" style={{ color: theme.text }}>{resolvedTitle}</h1>
            <HeaderActions />
          </div>
        </header>

        {/* Page content */}
        <main ref={mainContentRef} className="flex-1 min-h-0 overflow-y-auto pt-16 lg:pt-0 custom-scrollbar">
          {activeChat ? (
            <div className="h-full min-h-0 overflow-hidden">
              <MessageChat
                userId={activeChat.receiverId}
                userName={activeChat.userName}
                userPhotoId={activeChat.photoId}
                scrollToMessageId={activeChat.scrollToMessageId}
                onBack={() => setActiveChat(null)}
              />
            </div>
          ) : (
            <div className={contentClassName ?? "isx-page-shell"}>
              {children}
            </div>
          )}
        </main>
      </div>
      <UnsavedChangesDialog isOpen={showUnsavedDialog} onConfirm={handleLeaveWithoutSaving} onSave={handleSaveAndContinue} />
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
